<template>
  <sf-modal-portal
    modal-id="external-company-selector-modal"
    data-walkthrough-id="external-company-selector-modal"
    data-testid="external-company-selector-modal"
    size="l"
    class="sf-border-box"
    :scrollable-content="true"
    @close="onCloseAndDismiss"
  >
    <template #title>
      <span v-if="!isLoading">
        {{ I18n.t('company_links.select_external_accounts') }}
      </span>
    </template>
    <template #body>
      <div v-if="isLoading" class="sf-pad-3 sf-level sf-level-center">
        <div class="sf-spinner"></div>
      </div>
      <div v-else class="sf-grid sf-grid-fluid sf-grid-gutter-medium tw-px-32">
        <span v-if="existingCompanyLinks && existingCompanyLinks.length">
          <div class="sf-row sf-below-1">
            <div class="sf-col">
              <div class="sf-level sf-level-between sf-below-0">
                <div class="sf-level-item">
                  <span class="sf-font-s sf-font-medium tw-text-sf-gray-dark">
                    {{ I18n.t('company_links.select_linked_company_title') }}
                  </span>
                </div>
                <div class="sf-level-item">
                  <button
                    class="sf-btn sf-btn-small"
                    @click="openManageCompanyLinksModal"
                  >
                    <i class="icon-pencil"></i>
                    <span>
                      {{ I18n.t('company_links.manage_button') }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="sf-row sf-below-3">
            <div class="sf-col">
              <span class="sf-font-s tw-text-sf-gray-darker">
                {{ I18n.t('company_links.select_linked_company_description') }}
              </span>
            </div>
          </div>
          <div class="sf-row">
            <div
              class="sf-col sf-layer-border sf-layer-rounded"
              data-testid="linked-companies"
            >
              <div
                v-for="companyLink in effectiveLinkedCompanies"
                :key="companyLink.sourceCompanyId"
                class="sf-row sf-below-0"
                :class="companyLink.bottomBorderClass"
              >
                <div
                  class="sf-col sf-tab-item sf-level sf-level-between"
                  :data-testid="'c_' + companyLink.sourceCompanyId"
                >
                  <ExternalCompanyLabel
                    :company-id="companyLink.sourceCompanyId"
                    :company-name="companyLink.sourceCompanyName"
                    :last-synced-at="companyLink.lastSyncedAt"
                    :ledger-label="ledgerLabel"
                  />
                  <div class="sf-level-item">
                    <SfButton
                      :variant="'primary'"
                      :small="true"
                      :data-testid="'choose_' + companyLink.id"
                      @click="handleCompanySelected(companyLink)"
                    >
                      <span class="sf-font-xs">
                        {{ I18n.t('company_links.choose_button_label') }}
                      </span>
                      <span class="sf-icon icon-arrow-right"></span>
                    </SfButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </span>
        <span v-else>
          <CompanyLinksEmptyState />
        </span>
      </div>
    </template>
    <template #footer>
      <div
        v-if="existingCompanyLinks && existingCompanyLinks.length"
        class="sf-pad-2 tw-w-full"
      >
        <div class="sf-level sf-level-right">
          <SfButton variant="" @click="onCloseAndBack">{{
            I18n.t('common.form.cancel')
          }}</SfButton>
          <SfButton
            variant=""
            class="sf-disabled"
            @click="onSaveCompanySelection"
            >{{ I18n.t('common.save') }}</SfButton
          >
        </div>
      </div>
    </template>
  </sf-modal-portal>
</template>

<script setup lang="ts">
import { ExistingCompanyLink } from '@app/application/company_links/types'

import useI18n from '@app/shared/vue/composables/use-I18n'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'

import { useCompanyLinksStore } from '@app/application/company_links/store/use-company-links'
import { useExternalCompaniesStore } from '@app/application/company_links/store/use-external-companies'
import { useUserStore } from '@app/shared/pinia/user'
import { storeToRefs } from 'pinia'
import { ref, computed } from 'vue'

import CompanyLinksEmptyState from '@app/application/company_links/components/company-links-empty-state.vue'
import ExternalCompanyLabel from '@app/application/company_links/components/external-company-label.vue'

const I18n = useI18n()

interface Props {
  ledgerLabel: string
  currentUserId: number
}
const { currentUserId } = defineProps<Props>()

const isLoading = ref(false)

const companyLinksStore = useCompanyLinksStore()
const externalCompaniesStore = useExternalCompaniesStore()
const userStore = useUserStore()

userStore.setUserId(currentUserId)

const { existingCompanyLinks, initialized } = storeToRefs(companyLinksStore)

const loadCompanyLinksStore = async () => {
  if (!initialized.value) {
    isLoading.value = true
    await companyLinksStore.initExistingCompanyLinks()
    isLoading.value = false
  }
}
loadCompanyLinksStore()

const bottomBorderClass = (index: number) => {
  return {
    'sf-layer-border-bottom': index !== existingCompanyLinks.value.length - 1,
  }
}

const effectiveLinkedCompanies = computed(() => {
  return existingCompanyLinks.value.map((companyLink, index) => ({
    ...companyLink,
    bottomBorderClass: bottomBorderClass(index),
  }))
})

const openManageCompanyLinksModal = () => {
  window.modalCitizen.openModalPortal('manage-company-links-modal')
}

const onCloseAndDismiss = () => {
  externalCompaniesStore.clearSelectedCompanies()
  window.modalCitizen.closeModalPortal()
}

const onCloseAndBack = () => {
  window.modalCitizen.closeModalPortal()
}

const handleCompanySelected = (companyLink: ExistingCompanyLink) => {
  externalCompaniesStore.setSelectedCompanies([companyLink])
  window.modalCitizen.closeModalPortal()
}

const onSaveCompanySelection = () => {
  // TODO: Implement saving selected companies (for liquid input)
}
</script>
